import { RefCallback, useCallback, useMemo, useRef } from 'react';

export type ObserverCallback<TElement> = (elem?: TElement) => void;

interface ObserverProps<TElement> {
    callback: ObserverCallback<TElement>;
    /**
     * Конструктор ResizeObserver. Полезно для PiP/iframe - позволяет подсунуть
     * `pipWindow.ResizeObserver`, чтобы наблюдатель работал в контексте чужого окна.
     * По умолчанию - глобальный ResizeObserver текущего окна.
     */
    ResizeObserverCtor?: typeof ResizeObserver;
}

// https://reactjs.org/docs/hooks-faq.html#how-can-i-measure-a-dom-node
export const useResizeObserver = <TElement extends Element>({
    callback,
    ResizeObserverCtor,
}: ObserverProps<TElement>) => {
    const callbackRef = useRef<ObserverCallback<TElement>>();
    callbackRef.current = callback;

    const currentElem = useRef<TElement>();
    const observer = useMemo(() => {
        const RO = ResizeObserverCtor ?? ResizeObserver;
        return new RO(() => window.requestAnimationFrame(() => callbackRef.current?.(currentElem.current)));
    }, [ResizeObserverCtor]);

    const refCallback: RefCallback<TElement> = useCallback(
        (element) => {
            if (currentElem.current) {
                observer.unobserve(currentElem.current);
                currentElem.current = undefined;
            }

            if (element) {
                currentElem.current = element;
                observer.observe(element);
            }
        },
        [observer]
    );
    return { refCallback, ref: currentElem };
};
